/**
 * Tools > Bulk PDF export.
 *
 * Pick a document kind, a window and two optional filters, and take away one zip
 * of PDFs. The Perfex screen this replaces does the same thing blind: you press
 * Export and find out afterwards how many documents there were, or that there
 * were none. This one counts first, so the button says what it is about to do,
 * and it says out loud when the cap has clipped the result.
 */
import { useCallback, useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import {
  BULK_PDF_STATUS,
  BULK_PDF_TYPES,
  BulkPdfEmptyError,
  countBulkPdf,
  downloadBulkPdf,
  type BulkPdfCount,
  type BulkPdfFilters,
  type BulkPdfType,
} from "../../../lib/bulkPdf";
import { Btn, Card, CardHead, MenuField, MultiMenuField, PageHead, fmtNum, useToast } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { Field } from "../_shared/formBits";
import { DatePicker } from "@wa/ui";

export function BulkPdfPage({ user }: { user: StaffUser }) {
  const t = useT();
  const { locale } = useI18n();
  const toast = useToast();
  // The route already refuses without it; this only decides whether the button
  // is drawn, so a viewer who reached the page by URL is not offered a control
  // whose only possible answer is 403.
  const canExport = userCanPlatform(user, "manage:crm.sales");

  const [type, setType] = useState<BulkPdfType>("invoice");
  // Several at once: "unpaid and overdue" is one archive an operator wants, and
  // one status at a time made that two runs and two zips. Empty is every status.
  const [statuses, setStatuses] = useState<string[]>([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [clientTag, setClientTag] = useState("");
  const [count, setCount] = useState<BulkPdfCount | null>(null);
  const [counting, setCounting] = useState(false);
  const [busy, setBusy] = useState(false);

  const filters: BulkPdfFilters = {
    type,
    from: from || undefined,
    to: to || undefined,
    statuses,
    clientTag: clientTag.trim() || undefined,
  };

  // Switching the kind drops the ticks that belonged to the old one and keeps
  // the ones the new kind also has - every kind has a draft, and re-ticking it
  // after every type change would be busywork. The server would ignore a stale
  // status either way, but a dropdown reading "Expired" over a list of proposals
  // is a filter that looks applied and is not.
  const changeType = (next: BulkPdfType) => {
    setType(next);
    setStatuses((cur) => cur.filter((s) => BULK_PDF_STATUS[next].includes(s)));
  };

  const key = `${type}|${from}|${to}|${statuses.join(",")}|${clientTag.trim()}`;
  const recount = useCallback(
    async (signal: AbortSignal) => {
      setCounting(true);
      try {
        setCount(await countBulkPdf(filters, signal));
      } catch {
        if (!signal.aborted) setCount(null);
      } finally {
        if (!signal.aborted) setCounting(false);
      }
    },
    // Deliberately keyed on the flattened filters rather than on the object:
    // `filters` is rebuilt on every render, so depending on it would count on
    // every keystroke the page makes anywhere.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  );

  useEffect(() => {
    const ac = new AbortController();
    void recount(ac.signal);
    return () => ac.abort();
  }, [recount]);

  async function doExport() {
    if (busy) return;
    setBusy(true);
    try {
      const res = await downloadBulkPdf(filters);
      toast(
        res.rendered < res.matched
          ? t("tools.bulkPdf.doneCapped", { count: fmtNum(res.rendered), total: fmtNum(res.matched) })
          : t("tools.bulkPdf.done", { count: fmtNum(res.rendered) }),
      );
    } catch (err) {
      toast(err instanceof BulkPdfEmptyError ? t("tools.bulkPdf.none") : t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  const total = count?.total ?? 0;
  const willExport = count ? Math.min(count.total, count.cap) : 0;

  return (
    <PopHost>
      <PageHead title={t("tools.bulkPdf.title")} sub={t("tools.bulkPdf.intro")} />

      <Card>
        <CardHead title={t("tools.bulkPdf.filtersTitle")} sub={t("tools.bulkPdf.filtersSub")} />
        {/* Each `Field` is a fragment (a label plus its control), so every one
            of them needs a wrapper of its own or the grid would take the label
            and the field as two separate cells. The same shape `NewClientModal`
            uses. */}
        <div className="grid gap-x-5 px-[26px] pb-2 sm:grid-cols-2">
          <div>
            <Field label={t("tools.bulkPdf.type")}>
              <MenuField
                variant="form"
                value={type}
                onChange={changeType}
                label={t("tools.bulkPdf.type")}
                options={BULK_PDF_TYPES.map((v) => ({ value: v, label: t(`tools.bulkPdf.kind.${v}`) }))}
              />
            </Field>
          </div>

          <div>
            <Field label={t("tools.bulkPdf.status")}>
              <MultiMenuField
                variant="form"
                value={statuses}
                onChange={setStatuses}
                label={t("tools.bulkPdf.status")}
                allLabel={t("common.all")}
                countLabel={(n) => t("common.nSelected", { n })}
                clearLabel={t("common.clear")}
                options={BULK_PDF_STATUS[type].map((v) => ({ value: v, label: t(`sales.status.${v}`) }))}
              />
            </Field>
          </div>

          <div>
            <Field label={t("tools.bulkPdf.from")}>
              <DatePicker
                value={from}
                onChange={setFrom}
                toDate={to ? new Date(`${to}T00:00:00`) : undefined}
                locale={locale}
                placeholder={t("tools.bulkPdf.anyDate")}
                ariaLabel={t("tools.bulkPdf.from")}
                className="crm-fld crm-fld-date crm-fld-form w-full"
              />
            </Field>
          </div>

          <div>
            <Field label={t("tools.bulkPdf.to")}>
              <DatePicker
                value={to}
                onChange={setTo}
                fromDate={from ? new Date(`${from}T00:00:00`) : undefined}
                locale={locale}
                placeholder={t("tools.bulkPdf.anyDate")}
                ariaLabel={t("tools.bulkPdf.to")}
                className="crm-fld crm-fld-date crm-fld-form w-full"
              />
            </Field>
          </div>

          <div>
            <Field label={t("tools.bulkPdf.clientTag")}>
              <label className="crm-inp">
                <input
                  type="text"
                  value={clientTag}
                  onChange={(e) => setClientTag(e.target.value)}
                  placeholder={t("tools.bulkPdf.clientTagHint")}
                  autoComplete="off"
                />
              </label>
            </Field>
          </div>
        </div>

        <div className="crm-tfoot">
          <span className="crm-sub">
            {counting ? (
              t("tools.bulkPdf.counting")
            ) : count ? (
              count.capped
                ? t("tools.bulkPdf.countCapped", { count: fmtNum(willExport), total: fmtNum(total) })
                : t("tools.bulkPdf.count", { count: fmtNum(total) })
            ) : (
              t("common.error")
            )}
          </span>
          <span className="crm-push" />
          <Btn tone="brand" onClick={doExport} disabled={!canExport || busy || counting || !total}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
            {t("tools.bulkPdf.export")}
          </Btn>
        </div>
      </Card>

      <p className="crm-rep-note">{t("tools.bulkPdf.note")}</p>
    </PopHost>
  );
}
